/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */

import React, { useMemo, useCallback } from 'react';
import { buildChatAvailabilityUrl, usePageContext } from '@marriott/mi-shop-components';
import { SmallPropertyCard } from '../SmallPropertyCard/SmallPropertyCard';
import { MChipGroup } from '../MChipGroup';
import { MCarouselContainer } from '../MCarouselContainer';
import * as Styled from './MCategoryList.styles';
import { MCategoryListProps, CategoryItem, CategoryPropertyItem } from './MCategoryList.types';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { MResponseActions } from '../MResponseActions';
import { useHqvMarkdownComponents } from '../../hooks/useHqvMarkdownComponents';
import { getPropertyReviewUrl, getRawSearchEdgesTotal, getRawSearchTotal } from '../../utils';
import { PropertyCardProperty, PropertyCardSourceData } from '../../types';
import { fallbackImages, HWSBrands } from '../../organisms/OSearchResults/index.constant';
import { mapRateRequestToSpecialRates, buildChatSearchCriteriaFromAmpRequest } from '../../utils/ampRequestMapper';
import { MRW_CODE, P17_CODE } from '../MPropertyPriceSection/MPropertyPriceSection.constants';
import { trcRedirectionVarValue } from '../../constants';
import { computeRedirectionUrl } from '../../utils/computeRedirectionUrl';

/**
 * Check if the ampRequest contains points-related rate codes (MRW or P17)
 * This determines if the original search was a points search
 */
function isPointsSearchFromAmpRequest(ampRequest: any): boolean {
  if (!ampRequest) return false;

  const rateRequests = ampRequest?.productQueriesHospitality?.priceModifiers?.rateRequest;
  if (!rateRequests || !Array.isArray(rateRequests)) return false;

  // Check if any rate request has MRW or P17 code (points-related codes)
  return rateRequests.some(
    (req: { rateRequestValue?: string }) => req?.rateRequestValue === MRW_CODE || req?.rateRequestValue === P17_CODE
  );
}

interface SearchEdge {
  node?: {
    property?: {
      id?: string;
      seoNickname?: string;
      basicInformation?: {
        nameInDefaultLanguage?: string;
        priceDisplay?: string;
        openingDate?: string;
        bookable?: boolean;
        isAdultsOnly?: boolean;
        brand?: {
          id?: string;
          name?: string;
        };
      };
      contactInformation?: {
        address?: {
          city?: string;
          stateProvince?: {
            code?: string;
            description?: string;
          };
          country?: {
            code?: string;
            description?: string;
          };
        };
      };
      reviews?: {
        stars?: { count?: number };
        numberOfReviews?: { count?: number };
      };
      media?: {
        primaryImage?: {
          edges?: Array<{
            node?: {
              imageUrls?: {
                wideHorizontal?: string;
                classicHorizontal?: string;
                classic?: string;
                square?: string;
              };
            };
          }>;
        };
      };
    };
    distance?: number;
    rates?: Array<{
      rateModes?: {
        lowestAverageRate?: {
          amount?: {
            amount?: number;
            currency?: string;
            decimalPoint?: number;
          };
          totalAmount?: {
            amount?: number;
            currency?: string;
            decimalPoint?: number;
          };
          amountPlusMandatoryFees?: {
            amount?: number;
            currency?: string;
            decimalPoint?: number;
          };
          mandatoryFees?: {
            amount?: number;
            currency?: string;
            decimalPoint?: number;
          };
        };
      };
      rateCategory?: {
        code?: string;
        value?: string | null;
      };
      status?: {
        code?: string;
      };
    }>;
  };
}

export const MCategoryList: React.FC<MCategoryListProps> = ({
  uiData,
  viewData,
  onChipClick,
  onOpenHotelPanel,
  onViewAllProperties,
  messageId,
  thumbFeedback,
  onThumbFeedback,
}) => {
  // Page-level config for URL base
  const { NEXT_PUBLIC_AVAILABILITY_SEARCH_URL } = usePageContext();

  // Extract ampRequest and isFlexibleDateSearch from viewData for edge case handling
  const ampRequest = viewData?.ampRequest;
  const isFlexibleDateSearch = viewData?.isFlexDate ?? false;

  // Derive isPointsSearch from ampRequest instead of store state
  // This ensures the display mode matches the original search criteria for this message
  const isPointsSearch = useMemo(() => isPointsSearchFromAmpRequest(ampRequest), [ampRequest]);

  // Build chatSearchCriteria from ampRequest for URL construction
  const chatSearchCriteria = useMemo(() => {
    const llmMetadata = (viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery as any)
      ?.llmMetadata;
    return buildChatSearchCriteriaFromAmpRequest(ampRequest, llmMetadata, isFlexibleDateSearch);
  }, [ampRequest, viewData, isFlexibleDateSearch]);

  // Helper function to build availability URL for a property
  const buildPropertyAvailabilityUrl = (propertyId: string): string => {
    if (!NEXT_PUBLIC_AVAILABILITY_SEARCH_URL || !propertyId) return '';

    return buildChatAvailabilityUrl({
      baseUrl: NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      propertyCode: propertyId,
      chatSearchCriteria: chatSearchCriteria ?? undefined,
      forceRateCalendar: isFlexibleDateSearch,
    });
  };

  // Compute isSpecialRateSearch from ampRequest (same logic as MIntegratedImagery)
  const isSpecialRateSearch = useMemo(() => {
    if (!ampRequest) return false;

    const rateRequests = ampRequest?.productQueriesHospitality?.priceModifiers?.rateRequest;
    const { specialRates } = mapRateRequestToSpecialRates(rateRequests);

    const clusterCode = specialRates?.clusterCode ?? '';
    const corporateCode = specialRates?.corporateCode ?? '';
    const groupCode = specialRates?.groupCode ?? '';

    return Boolean(clusterCode || corporateCode || groupCode);
  }, [ampRequest]);

  // Extract childCount from ampRequest guestCounts (guests with age are children)
  const childCount = useMemo(() => {
    const guestCounts =
      ampRequest?.productQueriesHospitality?.roomStayQueryProduct?.baseHospitalityFacilityQuery?.guestCounts ?? [];
    return guestCounts.filter((g: any) => g.age !== null && g.age !== undefined).length;
  }, [ampRequest]);

  // Intercept hqv:// links in intro/followUp markdown so they open HQV with THIS
  // message's viewData (correct prices/criteria), matching MDefaultTemplate/MIntegratedImagery.
  const hqvComponents = useHqvMarkdownComponents(onOpenHotelPanel, viewData);

  // Helper to get the current locale for HWS URL construction
  const getCurrentLocale = useCallback((): string => {
    if (typeof document !== 'undefined' && document.documentElement?.lang) {
      return document.documentElement.lang;
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return 'en-US';
  }, []);

  // Helper to check if a brand is an HWS brand and get the redirect URL
  const getHwsRedirectUrl = useCallback(
    (brandCode?: string, propertyId?: string, propertyName?: string, seoNickname?: string | null): string | null => {
      if (!brandCode || !HWSBrands.includes(brandCode)) {
        return null;
      }
      const locale = getCurrentLocale();
      return computeRedirectionUrl(brandCode, propertyId, propertyName, trcRedirectionVarValue, locale, seoNickname);
    },
    [getCurrentLocale]
  );

  // Use edges from THIS message's viewData to ensure prices match the search criteria
  // that was active when this message was created. This prevents price inconsistency
  // when users modify search criteria or view older messages.
  const { edgesById, allEdges } = useMemo(() => {
    const map = new Map<string, SearchEdge>();
    const edges: SearchEdge[] = [];

    // Extract edges from the message-specific viewData passed as prop
    const msgEdges =
      (viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges as SearchEdge[]) ??
      [];
    msgEdges.forEach((edge: SearchEdge) => {
      const id = edge?.node?.property?.id;
      if (id) {
        map.set(id, edge);
        edges.push(edge);
      }
    });

    return { edgesById: map, allEdges: edges };
  }, [viewData]);

  const intro = typeof uiData?.intro === 'string' ? uiData.intro : '';
  const followUp = typeof uiData?.followUp === 'string' ? uiData.followUp : '';
  const categoryList = Array.isArray(uiData?.categoryList) ? uiData.categoryList : [];
  const chips = Array.isArray(uiData?.mChipGroup?.mQuickSelectChip) ? uiData.mChipGroup!.mQuickSelectChip : [];
  const totalResults =
    getRawSearchEdgesTotal(viewData as PropertyCardSourceData<SearchEdge>) ??
    getRawSearchTotal(viewData as PropertyCardSourceData<SearchEdge>) ??
    allEdges.length;

  const handlePropertyClick = useCallback(
    (edge: SearchEdge) => {
      const property = edge?.node?.property;
      if (!property) return;

      const brandCode = property?.basicInformation?.brand?.id;
      const hwsUrl = getHwsRedirectUrl(
        brandCode,
        property.id,
        property.basicInformation?.nameInDefaultLanguage,
        property.seoNickname
      );

      if (hwsUrl) {
        window.open(hwsUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (onOpenHotelPanel) {
        onOpenHotelPanel(property, property.id || '', { messageViewData: viewData });
      }
    },
    [getHwsRedirectUrl, onOpenHotelPanel, viewData]
  );

  const handleImageClick = useCallback(
    (edge: SearchEdge) => {
      const property = edge?.node?.property;
      if (!property) return;

      const brandCode = property?.basicInformation?.brand?.id;
      const hwsUrl = getHwsRedirectUrl(
        brandCode,
        property.id,
        property.basicInformation?.nameInDefaultLanguage,
        property.seoNickname
      );

      if (hwsUrl) {
        window.open(hwsUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (onOpenHotelPanel) {
        onOpenHotelPanel(property, property.id || '', { messageViewData: viewData, openGallery: true });
      }
    },
    [getHwsRedirectUrl, onOpenHotelPanel, viewData]
  );

  return (
    <Styled.Container>
      {intro && (
        <div className="t-font-s">
          <MarkdownPreview source={intro} components={hqvComponents} />
        </div>
      )}

      {categoryList.map((category: CategoryItem, categoryIdx: number) => {
        const categoryProperties = category.properties || [];

        return (
          <Styled.CategoryBlock key={`category-${categoryIdx}`}>
            <Styled.CategoryHeader>
              {category.categoryName && (
                <div className="t-subtitle-xl">
                  <MarkdownPreview source={category.categoryName} />
                </div>
              )}
            </Styled.CategoryHeader>

            {category.aiDescription && (
              <Styled.CategoryDescription className="t-font-s">{category.aiDescription}</Styled.CategoryDescription>
            )}

            <Styled.CarouselWrapper>
              <MCarouselContainer fixedSlideWidth={260} gap={16}>
                {categoryProperties.map((prop: CategoryPropertyItem, propIdx: number) => {
                  const edge = edgesById.get(prop.propertyId);
                  if (!edge) return null;

                  const property = edge.node?.property;
                  const imageUrls = property?.media?.primaryImage?.edges?.[0]?.node?.imageUrls;
                  const imageUrl =
                    imageUrls?.wideHorizontal ||
                    imageUrls?.classicHorizontal ||
                    imageUrls?.classic ||
                    imageUrls?.square;

                  const ratingValue = property?.reviews?.stars?.count || 0;
                  const reviewCountValue = property?.reviews?.numberOfReviews?.count || 0;
                  const reviewUrl = getPropertyReviewUrl(property as PropertyCardProperty | undefined) || '#';

                  return (
                    <SmallPropertyCard
                      key={`${prop.propertyId}-${propIdx}`}
                      fallbackImageUrl={fallbackImages.Wide}
                      imageUrl={imageUrl}
                      name={property?.basicInformation?.nameInDefaultLanguage || 'Property'}
                      location={edge.node?.distance}
                      locationSummary={{
                        city: property?.contactInformation?.address?.city,
                        state: property?.contactInformation?.address?.stateProvince?.code,
                        country: property?.contactInformation?.address?.country?.code,
                      }}
                      ratingInfo={{
                        isMax: false,
                        reviewUrl,
                        reviewTitle: `${ratingValue.toFixed(1)} stars of ${reviewCountValue.toLocaleString()} reviews`,
                        ratingValue,
                        hasRating: ratingValue > 0,
                        reviewCountValue,
                      }}
                      description={prop.aiDescription}
                      priceDisplay={property?.basicInformation?.priceDisplay}
                      edge={edge as Record<string, unknown>}
                      property={property as Record<string, unknown>}
                      propertyId={prop.propertyId}
                      onClick={() => handlePropertyClick(edge)}
                      onImageClick={() => handleImageClick(edge)}
                      priceDisplayMode={isPointsSearch ? 'points' : 'cash'}
                      soldOutLabel="Sold Out"
                      rateUnavailableLabel="Rate Unavailable"
                      isSpecialRateSearch={isSpecialRateSearch}
                      isFlexibleDateSearch={isFlexibleDateSearch}
                      openingDate={property?.basicInformation?.openingDate}
                      ampRequest={ampRequest}
                      callToBookLabel="Call to Book"
                      openingSoonLabel="Opening"
                      openingSoonDateFormat="MMMM YYYY"
                      isBookable={property?.basicInformation?.bookable}
                      maxOccupancyLabel="To see rate, edit guests or rooms."
                      adultsOnlyLabel="Please remove child guest(s)."
                      isAdultsOnly={property?.basicInformation?.isAdultsOnly}
                      childCount={childCount}
                      viewRatesURL={buildPropertyAvailabilityUrl(prop.propertyId)}
                      viewRatesLabel="View Rates"
                    />
                  );
                })}
              </MCarouselContainer>
            </Styled.CarouselWrapper>
          </Styled.CategoryBlock>
        );
      })}

      <MResponseActions
        messageId={messageId}
        allowViewAllProperties
        viewAllCopy="categoryProperties"
        totalResults={totalResults}
        thumbFeedback={thumbFeedback}
        onThumbsUp={
          onThumbFeedback
            ? () => {
                if (thumbFeedback) return;
                onThumbFeedback('up');
              }
            : undefined
        }
        onThumbsDown={
          onThumbFeedback
            ? () => {
                if (thumbFeedback) return;
                onThumbFeedback('down');
              }
            : undefined
        }
        onViewAll={() => onViewAllProperties?.(viewData)}
      />

      {/* Optional trailing guidance from formatter */}
      {followUp ? (
        <div className="pr-4 t-font-s">
          <MarkdownPreview source={followUp} components={hqvComponents} />
        </div>
      ) : null}
      {chips.length > 0 && (
        <Styled.ChipsWrapper>
          <MChipGroup
            chips={chips.map((chip: { text?: string }) => ({
              aBody: {
                bodyText: chip?.text || '',
              },
              onClick: () => {
                if (chip?.text) {
                  onChipClick?.(chip.text);
                }
              },
            }))}
          />
        </Styled.ChipsWrapper>
      )}
    </Styled.Container>
  );
};

export type { MCategoryListProps } from './MCategoryList.types';
